/**
 * The Align class aligns a child element relative to a parent element using a specified alignment method.
 */

export class Align {
  /**
   * Creates an instance of the Align class.
   * @param {string} parentSelector - The CSS selector for the parent element.
   * @param {string} childSelector - The CSS selector for the child element(s).
   * @param {function} alignment - The alignment method to use.
   * @param {number} delta - If you need extra fine tuning.
   * @param {boolean} multiple - Whether to align multiple child elements.
   */
  constructor(parentSelector, childSelector, alignment, options = {}) {
    const { delta = 0, multiple = false } = options;
    this.parent = document.querySelector(parentSelector);
    this.child = multiple
      ? Array.from(document.querySelectorAll(childSelector))
      : document.querySelector(childSelector);
    this.elements = Array.isArray(this.child) ? this.child : [this.child];
    this.alignment = alignment;
    this.delta = delta;
    if (this.parent && this.child) {
      this.elements?.forEach((el) => {
        this.alignment?.(this.parent, el, this.delta);
      });
      window.addEventListener("resize", () => {
        this.elements?.forEach((el) => {
          this.alignment?.(this.parent, el, this.delta);
        });
      });
    }
  }

  static center = (parent, child, delta = 0) => {
    const parentWidth = parent.getBoundingClientRect().width;
    const childWidth = child.getBoundingClientRect().width;
    const leftMargin = (parentWidth - childWidth) / 2;

    const {
      paddingLeft,
      paddingRight,
      borderLeftWidth,
      borderRightWidth,
      boxSizing,
    } = window.getComputedStyle(parent);

    const space =
      boxSizing === "border-box"
        ? paddingLeft + paddingRight + borderLeftWidth + borderRightWidth
        : 0;

    child.style.transform = `translateX(${leftMargin - space + delta}px)`;
  };

  static alignRight = (parent, child, delta = 0) => {
    const parentWidth = parent.getBoundingClientRect().width;
    const childWidth = child.getBoundingClientRect().width;
    const parentStyle = window.getComputedStyle(parent);
    const boxSizing = parentStyle.boxSizing;
    const paddingLeft = parseInt(parentStyle.paddingLeft);
    const paddingRight = parseInt(parentStyle.paddingRight);
    const translateX =
      boxSizing === "border-box"
        ? parentWidth - childWidth
        : parentWidth - childWidth - paddingLeft - paddingRight;
    child.style.transform = `translateX(${translateX + delta}px)`;
  };

  static alignTop = (parent, child, delta = 0) => {
    const parentRect = parent.getBoundingClientRect();
    const childRect = child.getBoundingClientRect();
    const parentStyle = window.getComputedStyle(parent);
    const paddingTop = parseInt(parentStyle.paddingTop);
    const boxSizing = parentStyle.boxSizing;
    const translateY =
      boxSizing === "border-box" ? paddingTop : paddingTop - parentRect.top;
    child.style.transform = `translateY(${translateY + delta}px)`;
  };

  static alignBottom = (parent, child, delta = 0) => {
    const parentRect = parent.getBoundingClientRect();
    const childRect = child.getBoundingClientRect();
    const parentStyle = window.getComputedStyle(parent);
    const paddingBottom = parseInt(parentStyle.paddingBottom);
    const boxSizing = parentStyle.boxSizing;
    const translateY =
      boxSizing === "border-box"
        ? parentRect.height - childRect.height - paddingBottom
        : parentRect.bottom - childRect.height - paddingBottom;
    child.style.transform = `translateY(${translateY + delta}px)`;
  };
}
